import type { LocalDate } from 'src/utilities/local-date.js';
import { DayOfWeek, DayOfWeekValues } from 'src/utilities/local-date.js';
import { t } from 'src/i18n';
import { getWeekdayName, toDayOfWeek } from '../date.js';
import { BaseInstanceRule } from './base-instance-rule.js';
import type { WeekdayStr } from '../weekdayStr';

export class WeeklyInstanceRule extends BaseInstanceRule {
  // Bygges lazy via gettere slik at i18n-`t()` kjøres ved kalltid, ikke import.
  private static get intervalDescriptionPrefix(): string[] {
    return [
      t('recurrence.weekly.multiDayPrefix_1'),
      t('recurrence.weekly.multiDayPrefix_2'),
      t('recurrence.weekly.multiDayPrefix_3'),
      t('recurrence.weekly.multiDayPrefix_4'),
      t('recurrence.weekly.multiDayPrefix_5'),
      t('recurrence.weekly.multiDayPrefix_6'),
    ];
  }

  private static get intervalDescriptionsForAllDays(): string[] {
    return [
      t('recurrence.weekly.everyAllDays_1'),
      t('recurrence.weekly.everyAllDays_2'),
      t('recurrence.weekly.everyAllDays_3'),
      t('recurrence.weekly.everyAllDays_4'),
      t('recurrence.weekly.everyAllDays_5'),
      t('recurrence.weekly.everyAllDays_6'),
    ];
  }

  private static get intervalDescriptionPrefixForSingleDay(): string[] {
    return [
      t('recurrence.weekly.singleDayPrefix_1'),
      t('recurrence.weekly.singleDayPrefix_2'),
      t('recurrence.weekly.singleDayPrefix_3'),
      t('recurrence.weekly.singleDayPrefix_4'),
      t('recurrence.weekly.singleDayPrefix_5'),
      t('recurrence.weekly.singleDayPrefix_6'),
    ];
  }

  private readonly dayOfWeekSet: Set<DayOfWeek> = new Set<DayOfWeek>();

  private readonly byWeekDay: WeekdayStr[];

  public constructor(byWeekDay: WeekdayStr[]) {
    super();
    this.byWeekDay = byWeekDay;

    this.byWeekDay.forEach((b) => {
      this.dayOfWeekSet.add(toDayOfWeek(b));
    });
  }

  public matches(date: LocalDate): boolean {
    return this.dayOfWeekSet.has(date.dayOfWeek());
  }

  public optionsToString(interval: number): string {
    return 'FREQ=WEEKLY;INTERVAL=' + interval + ';BYDAY=' + this.byWeekDay.join(',');
  }

  public scheduleDescription(interval: number): string {
    if (this.dayOfWeekSet.size === 7) {
      return WeeklyInstanceRule.intervalDescriptionsForAllDays[interval - 1];
    } else {
      const s: string[] = DayOfWeekValues.filter((d) => this.dayOfWeekSet.has(d)).map((d) => getWeekdayName(d));

      if (s.length === 1) {
        return WeeklyInstanceRule.intervalDescriptionPrefixForSingleDay[interval - 1] + s[0];
      } else {
        return (
          WeeklyInstanceRule.intervalDescriptionPrefix[interval - 1] +
          s.slice(0, s.length - 1).join(', ') +
          t('recurrence.weekly.dayJoiner') +
          s[s.length - 1]
        );
      }
    }
  }

  public expand(start: LocalDate, end: LocalDate, interval: number): LocalDate[] {
    if (interval === 1 || this.byWeekDay.length === 1) {
      return super.expand(start, end, interval);
    } else {
      const s = super.expand(start, end, 1);
      const mondayOfStartWeek = start.withPreviousOrSame(DayOfWeek.MONDAY);
      return s.filter((d) => {
        const monday = d.withPreviousOrSame(DayOfWeek.MONDAY);
        const distance = mondayOfStartWeek.until(monday) / 7;
        return distance % interval === 0;
      });
    }
  }
}
